import type { QueryField } from '../index'

export type NestedCalendarDefinitionGroup = {
  key: string
  label: string
  definitionHandle: string
  fields: QueryField[]
  selected: QueryField | null
}

export const CALENDAR_NESTED_DEFINITION_HANDLES = ['calendar/color'] as const

const CALENDAR_SESSION_EXCLUDED_DEFINITION_HANDLES = [
  'calendar/recurrence',
  'calendar/series_id',
  'calendar/original_start',
] as const

const leafHandle = (value: string): string => value.split('.').pop() || value

export const isCalendarSessionField = (field: QueryField): boolean => {
  const dataType = field.dataType
  if (
    dataType !== 'DATE' &&
    dataType !== 'DATE_TIME' &&
    dataType !== 'TIME'
  ) {
    return false
  }
  const definitionHandle = field.definitionHandle
  if (
    typeof definitionHandle === 'string' &&
    (
      CALENDAR_SESSION_EXCLUDED_DEFINITION_HANDLES as readonly string[]
    ).includes(definitionHandle)
  ) {
    return false
  }
  return true
}

export const getCalendarRelationFields = (fields: QueryField[]): QueryField[] =>
  fields.filter((field) => field.dataType === 'RELATION')

export const shouldShowCalendarRelationPicker = (
  fields: QueryField[],
): boolean => getCalendarRelationFields(fields).length > 1

export const inferCalendarRelationField = (
  fields: QueryField[],
): QueryField | null => {
  const relations = getCalendarRelationFields(fields)
  const named = relations.find((field) => leafHandle(field.value) === 'calendar')
  if (named) return named
  if (relations.length === 1) return relations[0] ?? null
  return null
}

export const getNestedCalendarDefinitionGroups = (
  calendarField: QueryField | null | undefined,
  calendarFieldMap: Record<string, string> | undefined,
  fields: QueryField[],
): NestedCalendarDefinitionGroup[] => {
  if (!calendarField) return []
  const leaf = leafHandle(calendarField.value)
  const prefixes = [`${leaf}.`, `${calendarField.value}.`]
  const seen = new Set<string>()
  const nested = fields.filter((field) => {
    const value = String(field.value || '')
    if (seen.has(value)) return false
    const definitionHandle = field.definitionHandle
    const allowlisted =
      typeof definitionHandle === 'string' &&
      (
        CALENDAR_NESTED_DEFINITION_HANDLES as readonly string[]
      ).includes(definitionHandle)
    const matches =
      allowlisted && prefixes.some((prefix) => value.startsWith(prefix))
    if (matches) seen.add(value)
    return matches
  })
  const byDefinition = new Map<string, QueryField[]>()
  for (const field of nested) {
    const definitionHandle = field.definitionHandle
    if (!definitionHandle) continue
    const list = byDefinition.get(definitionHandle) ?? []
    list.push(field)
    byDefinition.set(definitionHandle, list)
  }
  return [...byDefinition.entries()].map(([definitionHandle, groupFields]) => {
    const mapped =
      calendarFieldMap?.[definitionHandle] ??
      calendarFieldMap?.[definitionHandle.split('/').pop() ?? '']
    const selected =
      groupFields.find((field) => {
        const fieldLeaf = leafHandle(field.value)
        return mapped === field.value || mapped === fieldLeaf
      }) ??
      (groupFields.length === 1 ? (groupFields[0] ?? null) : null)
    return {
      key: `calendar-nested-${definitionHandle}`,
      label: definitionHandle.split('/').pop() || definitionHandle,
      definitionHandle,
      fields: groupFields,
      selected,
    }
  })
}
